import { useEffect, useState } from "react";
import { Loader2, ScanSearch, ChevronDown, ChevronRight, CheckCircle2, Download, Search } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const ACTIONS = [
  ["primary", "Primary", "text-gold-300 border-gold-500/50"],
  ["keep", "Keep", "text-emerald-300 border-emerald-400/40"],
  ["remove_redundant", "Remove — redundant", "text-white/50 border-white/20"],
  ["remove_low_quality", "Remove — low quality", "text-red-300 border-red-400/40"],
  ["review_mismatch", "Mismatch — comment", "text-amber-300 border-amber-400/40"],
];
const actionCls = (a) => ACTIONS.find(([k]) => k === a)?.[2] || "text-white/40 border-white/20";

function Thumb({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/ew/training_image/${id}`).then((r) => on && setSrc(`data:image/jpeg;base64,${r.data.image_b64}`)).catch(() => {});
    return () => { on = false; };
  }, [id]);
  return src ? <img src={src} alt="damage" className="h-24 w-32 object-cover border border-gold-500/20" />
    : <div className="h-24 w-32 bg-navy-950 border border-gold-500/10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-white/20" /></div>;
}

function RequestCard({ r, onSaved }) {
  const [open, setOpen] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const act = (p) => overrides[p.filename || p.id] || p.action;
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/ew/curation/${encodeURIComponent(r.request_ref)}`, { status: "reviewed", overrides });
      toast.success(`${r.request_ref} marked reviewed`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="border border-gold-500/15 bg-navy-900/40" data-testid={`radar-req-${r.request_ref}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gold-500/5">
        {open ? <ChevronDown className="h-4 w-4 text-gold-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />}
        <Mono className="text-gold-300 text-[0.6rem] shrink-0">{r.request_ref}</Mono>
        <span className="text-white/70 text-xs truncate">{r.road_name || "—"}</span>
        <span className="text-white/40 text-[0.65rem] truncate hidden md:block">{r.tmr_code}</span>
        <span className="ml-auto flex items-center gap-2 shrink-0">
          {r.tmr_submission_id && <Mono className="text-[0.5rem] text-sky-300/70">{r.tmr_submission_id}</Mono>}
          <Mono className="text-[0.5rem] text-white/40">{r.assessed_count}/{r.photo_count} assessed</Mono>
          {r.review_status === "reviewed"
            ? <span className="text-emerald-300 text-[0.6rem] font-mono border border-emerald-400/40 px-1.5 py-0.5">REVIEWED</span>
            : <span className="text-white/40 text-[0.6rem] font-mono border border-white/20 px-1.5 py-0.5">PENDING</span>}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {r.photos.map((p) => (
            <div key={p.id} className="flex items-start gap-3 border-t border-gold-500/10 pt-3">
              <Thumb id={p.id} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {p.severity != null && <Mono className="text-[0.5rem] text-red-300">SEV {p.severity}</Mono>}
                  {p.quality != null && <Mono className="text-[0.5rem] text-white/40">QUALITY {p.quality}</Mono>}
                  {p.agrees === false && <Mono className="text-[0.5rem] text-amber-300">LABEL MISMATCH</Mono>}
                  {p.action === "pending_ai" && <Mono className="text-[0.5rem] text-white/30">AI ASSESSMENT PENDING</Mono>}
                </div>
                {p.note && <p className="text-white/50 text-xs font-light mt-1">{p.note}</p>}
                <p className="text-white/25 text-[0.6rem] font-mono mt-1 truncate">{p.filename}</p>
              </div>
              {p.action !== "pending_ai" && (
                <select value={act(p)} onChange={(e) => setOverrides((o) => ({ ...o, [p.filename || p.id]: e.target.value }))}
                  data-testid={`radar-action-${p.id}`}
                  className={`bg-navy-950 border text-xs px-2 py-1.5 shrink-0 ${actionCls(act(p))}`}>
                  {ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              )}
            </div>
          ))}
          <button onClick={save} disabled={saving} data-testid={`radar-review-save-${r.request_ref}`}
            className="bg-gold-500 text-navy-950 font-semibold text-xs px-4 py-2 hover:bg-gold-400 disabled:opacity-50 inline-flex items-center gap-2">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Mark reviewed
          </button>
        </div>
      )}
    </div>
  );
}

export default function RadarReview() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/ew/curation", { params: { status, q: q || undefined, skip: page * 25, limit: 25 } })
      .then((r) => setData(r.data)).catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  };
  useEffect(load, [status, page]); // eslint-disable-line react-hooks/exhaustive-deps
  const token = localStorage.getItem("mdau_token");
  return (
    <div className="space-y-5" data-testid="radar-review-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-light text-3xl flex items-center gap-3"><ScanSearch className="h-7 w-7 text-gold-400" /> RADAR review</h1>
          <p className="text-white/40 text-sm font-light mt-1">TMR-approved corpus — review the AI photo curation, then export the Manus packs.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API}/ew/manus_pack?token=${token}&fmt=csv`} target="_blank" rel="noreferrer" data-testid="radar-manus-pack-btn"
            className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs hover:bg-gold-500/10">
            <Download className="h-3.5 w-3.5" /> Manus curation pack
          </a>
          <a href={`${API}/ew/submission_pack?token=${token}`} target="_blank" rel="noreferrer" data-testid="radar-submission-pack-btn"
            className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-xs hover:bg-gold-400">
            <Download className="h-3.5 w-3.5" /> Finish submissions pack
          </a>
        </div>
      </div>
      {data && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="border border-gold-500/25 text-white/60 px-2 py-1 text-[0.65rem] font-mono" data-testid="radar-stat-corpus">{data.corpus} photos harvested</span>
          <span className="border border-sky-400/30 text-sky-300 px-2 py-1 text-[0.65rem] font-mono" data-testid="radar-stat-assessed">{data.assessed} AI-assessed</span>
          <span className="border border-emerald-400/30 text-emerald-300 px-2 py-1 text-[0.65rem] font-mono" data-testid="radar-stat-reviewed">{data.reviewed} reviewed</span>
          <span className="text-white/40 text-[0.65rem] font-mono">{data.total} requests {status !== "all" ? `(${status})` : ""}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "reviewed", "all"].map((s) => (
          <button key={s} onClick={() => { setStatus(s); setPage(0); }} data-testid={`radar-filter-${s}`}
            className={`px-3 py-1.5 text-xs border ${status === s ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/25 text-white/50 hover:text-white"}`}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2 text-white/30" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(0), load())}
            placeholder="GR ref or road…" data-testid="radar-search"
            className="bg-navy-950 border border-gold-500/25 pl-8 pr-3 py-1.5 text-xs w-56" />
        </div>
      </div>
      {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400 mx-auto" /></div> : (
        <div className="space-y-2">
          {(data?.requests || []).map((r) => <RequestCard key={r.request_ref} r={r} onSaved={load} />)}
          {data?.requests?.length === 0 && <div className="p-10 text-center text-white/40 text-sm">Nothing here.</div>}
        </div>
      )}
      {data && data.total > 25 && (
        <div className="flex items-center gap-3">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="border border-gold-500/25 px-3 py-1.5 text-xs text-white/60 disabled:opacity-30">← Prev</button>
          <Mono className="text-[0.55rem] text-white/40">Page {page + 1} of {Math.ceil(data.total / 25)}</Mono>
          <button disabled={(page + 1) * 25 >= data.total} onClick={() => setPage((p) => p + 1)} className="border border-gold-500/25 px-3 py-1.5 text-xs text-white/60 disabled:opacity-30">Next →</button>
        </div>
      )}
    </div>
  );
}
